import React from "react";
import { Paper, Table, TableBody, TableContainer, TableFooter, TableHead, TableRow } from "@mui/material";
import { useSelector } from "react-redux";
import { BucketNodeItem, BudgetTransaction, CurrentUser, FamilyBucketLabel, Grouping, StyledTableCell, StyledTableRow, USCurrency } from "../../types";

export type GroupingKey = string;

interface ReportPreviewTablesProps {
	groups: Grouping[];
	columns: string[];
	grouping: GroupingKey;
	className?: string;
}

const isBucketKey = (key: string) => !!key && key.toLowerCase().startsWith('bucket:');
const bucketTypeFromKey = (key: string) => key.substring('bucket:'.length);

const ReportPreviewTables = (props: ReportPreviewTablesProps) => {
	const { groups, columns, grouping, className } = props;
	const user: CurrentUser = useSelector((state: any) => state.userData.value);

	function GetColumnLabel(column: string) {
		if (isBucketKey(column)) {
			const typeKey = bucketTypeFromKey(column);
			return FamilyBucketLabel(user, typeKey, typeKey);
		}
		return FamilyBucketLabel(user, column, column);
	}

	const renderBucketCell = (item: BudgetTransaction, col: string) => {
		const typeKey = bucketTypeFromKey(col);
		const nodes: BucketNodeItem[] = (item.Buckets?.[typeKey] ?? []) as BucketNodeItem[];
		return nodes.map(n => n.displayName).filter(Boolean).join(', ');
	};

	return (
		<>
			{groups.map((group: Grouping, i: number) => {
				const groupTotal = group.Value.reduce((tot: number, b: BudgetTransaction) => b.Amount.amount + tot, 0);
				return (
					<div key={i}>
						{grouping !== "" && (
							<h5 className="customReport-groupHeader">
								{['Date', 'Merchant'].includes(grouping) && group.Key}
								{['Budget', 'SubBudget'].includes(grouping) && group.Key?.description}
								{grouping === 'User' && `${group.Key?.firstName} ${group.Key?.lastName}`}
								{isBucketKey(grouping) && (group.Key || '')}
								{" "}- {USCurrency(groupTotal)}
							</h5>
						)}
						<TableContainer component={Paper} className={className ?? "report"}>
							<Table>
								<TableHead>
									<TableRow>
										{columns.map((col: string) => (
											<StyledTableCell key={col}>{GetColumnLabel(col)}</StyledTableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{(group.Value as BudgetTransaction[]).map((item: BudgetTransaction, idx: number) => (
										<StyledTableRow key={item.Transaction.accountTransactionId + '_' + item.Amount.budgetId + '_' + idx}>
											{columns.map((col: string) => {
												if (col === "Date") return <StyledTableCell key={col}>{item.Transaction.transactionDate.toString()}</StyledTableCell>;
												if (col === "Name") return <StyledTableCell key={col}>{item.Transaction.created}</StyledTableCell>;
												if (col === "Merchant") return <StyledTableCell key={col}>{item.Transaction.merchant}</StyledTableCell>;
												if (col === "Budget") return <StyledTableCell key={col}>{item.Budget?.description}</StyledTableCell>;
												if (col === "SubBudget") return <StyledTableCell key={col}>{(item as any).SubBudget?.description}</StyledTableCell>;
												if (isBucketKey(col)) return <StyledTableCell key={col}>{renderBucketCell(item, col)}</StyledTableCell>;
												if (col === "Description") return <StyledTableCell key={col}>{item.Amount.details}</StyledTableCell>;
												if (col === "Amount") return <StyledTableCell key={col}>{USCurrency(item.Amount.amount)}{!item.Transaction.hasReceipt ? '*' : ''}</StyledTableCell>;
												return <StyledTableCell key={col}></StyledTableCell>;
											})}
										</StyledTableRow>
									))}
								</TableBody>
								<TableFooter>
									<StyledTableRow>
										{columns.map((col: string) => {
											if (col === "Description") return <StyledTableCell key={col}><b>TOTAL</b></StyledTableCell>;
											if (col === "Amount") return <StyledTableCell key={col}><b>{USCurrency(groupTotal)}</b></StyledTableCell>;
											return <StyledTableCell key={col}></StyledTableCell>;
										})}
									</StyledTableRow>
								</TableFooter>
							</Table>
						</TableContainer>
					</div>
				);
			})}
		</>
	);
};

export default ReportPreviewTables;
